import { useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  COMMUNITY_HERO, GREEK_PURSUIT, NO_COST_NOTE, GREEK_PRODUCTS,
  heroDealPayload, pursuitDealPayload, payloadProblems, canNominateHero,
  optimisticPatch,
} from './recognition.js'
import {
  CHEER_PARTNERSHIP, CHEER_PROGRAMS, PARTNERSHIP_DEFINITION,
  partnershipDealPayload, partnershipProblems, optimisticCheerPatch,
} from './cheer.js'
import { hubspotDealUrl } from './nextStep.js'
import { api } from '../../lib/api.js'
import styles from './RecognitionModal.module.css'

/**
 * THE CREATE FLOW — "Nominate Community Hero" (police / fire) and
 * "Start pursuit" (Greek). **This writes.**
 *
 * Both create a HubSpot DEAL associated to the organization's company record:
 * custom company properties can't be created through the connector, but a deal
 * natively carries an owner, a stage, a close date and a name, which is exactly
 * what a nomination and a pursuit are. See `recognition.js` for the model.
 *
 * THE WRITE IS LIVE (v1.30). `POST /api/recognition-deal` shipped, so the
 * `?recog=1` gate that stood in for it is gone and the button creates a real
 * deal. Three outcomes, all of them real — there is no fake success anywhere in
 * this file:
 *
 *  - **CREATED (201)** — the deal id, and a link straight to it in HubSpot.
 *  - **ALREADY RECORDED (200, `duplicate`)** — the same nomination submitted
 *    twice is ONE nomination. Reported as the success it is, naming the deal
 *    that already exists, rather than as an error or as a second create.
 *  - **CONFLICT (409, `hero_conflict`)** — this department already has an
 *    active Community Hero. The backend re-decided one-hero-per-precinct and
 *    refused; **nothing was created or changed**, and the modal says so with the
 *    conflicting nomination named and linked.
 *
 * Anything else surfaces as the error it is, with the backend's own message.
 */
/**
 * A DEMO RECORD IS NOT A HUBSPOT RECORD. `?demo=1` rows carry ids like
 * `police-3`, so a create would either 400 on the backend's id check or — far
 * worse if the shape ever changed — write a fictional nomination against a real
 * company. The write is refused here, visibly, rather than left to fail.
 */
function isDemoRecord(record) {
  const id = String(record?.sourceId ?? '').trim()
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === '1') return true
  return !!id && !/^\d+$/.test(id)
}

export default function RecognitionModal({ kind, record, onClose, onCreated }) {
  const hero = kind === COMMUNITY_HERO
  // THE CHEER GYM PARTNERSHIP — the same create, a third kind. The deal opens
  // the pursuit; it does NOT sign a contract and it does NOT launch a jewelry
  // program, both of which are human actions on the deal in HubSpot.
  const cheer = kind === CHEER_PARTNERSHIP
  const demo = isDemoRecord(record)

  const [heroName, setHeroName] = useState('')
  const [rank, setRank] = useState('')
  const [years, setYears] = useState('')
  const [citation, setCitation] = useState('')
  const [product, setProduct] = useState(() => (kind === CHEER_PARTNERSHIP ? CHEER_PROGRAMS[0] : GREEK_PRODUCTS[0]))
  const [owner, setOwner] = useState(record?.rep || '')
  const [pursuitDate, setPursuitDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [conflict, setConflict] = useState(null)
  const [result, setResult] = useState(null)

  // The CLIENT gate — UX only. The backend re-decides it, which is why a 409 is
  // handled below even though this check should have prevented the request.
  const gate = hero ? canNominateHero(record) : { allowed: true, reason: '' }

  const payload = useMemo(() => {
    const ownerId = record?.raw?.hubspot_owner_id || record?.raw?.ownerId || ''
    if (hero) {
      return heroDealPayload(record, {
        hero: { name: heroName, rank, yearsOfService: years, citation },
        owner,
        ownerId,
      })
    }
    if (cheer) return partnershipDealPayload(record, { program: product, owner, ownerId, pursuitDate })
    return pursuitDealPayload(record, { product, owner, ownerId, pursuitDate })
  }, [hero, cheer, record, heroName, rank, years, citation, owner, product, pursuitDate])

  const problems = cheer ? partnershipProblems(payload) : payloadProblems(payload)
  const ready = problems.length === 0 && gate.allowed && !demo
  const done = !!result

  const submit = async () => {
    if (!ready || busy || done) return
    setBusy(true)
    setError('')
    setConflict(null)
    try {
      const res = await api.recognitionDeal(payload)
      setResult(res)
      // The board updates from what the backend actually returned — an id and a
      // created/duplicate verdict — not from the fact that the call didn't throw.
      const patch = cheer ? optimisticCheerPatch(payload, res) : optimisticPatch(kind, payload, res)
      onCreated?.({ kind, record, payload, result: res, patch })
    } catch (err) {
      // ONE ACTIVE HERO PER PRECINCT, decided server-side. This is not a failure
      // to report as "something went wrong": it is the rule working, so it gets
      // its own state naming the nomination that holds the slot.
      if (err?.status === 409 && err?.body?.conflict) {
        setConflict({ ...err.body.conflict, message: err.body.message || err.message })
      } else if (err?.status === 400 && Array.isArray(err?.body?.problems)) {
        setError(`The nomination was rejected: ${err.body.problems.join('; ')}`)
      } else if (err?.missingRoute) {
        setError('POST /api/recognition-deal is not answering on the backend (404). Nothing was written.')
      } else {
        setError(err?.body?.message || err?.message || 'Could not create the deal. Nothing was written.')
      }
    } finally {
      setBusy(false)
    }
  }

  const createdUrl = hubspotDealUrl(result?.id)
  const conflictUrl = hubspotDealUrl(conflict?.id)

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <div>
            <div className="eyebrow">{record?.name}</div>
            <h2 className={styles.title}>
              {hero ? 'Nominate a Community Hero' : cheer ? 'Start a gym partnership pursuit' : 'Start a product pursuit'}
            </h2>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className={styles.body}>
          {/* ONE HERO PER PRECINCT — stated before the form, not discovered
              after filling it in. */}
          {!gate.allowed && !done && (
            <div className={styles.blocked}>
              <strong>Already has a Community Hero.</strong> {gate.reason}
            </div>
          )}

          {/* THE SERVER'S REFUSAL. The rule was re-decided on the backend and it
              said no — with the deal that holds the slot, named and linked. */}
          {conflict && (
            <div className={styles.blocked}>
              <strong>Refused — one active {`Community Hero`} per department.</strong>
              <div className={styles.conflictMsg}>{conflict.message}</div>
              <div className={styles.conflictDeal}>
                Holding the slot: <strong>{conflict.heroName || conflict.dealname || `deal ${conflict.id}`}</strong>
                {conflict.stage && <span className={styles.conflictStage}>{conflict.stage}</span>}
                {conflictUrl && (
                  <a className={styles.conflictLink} href={conflictUrl} target="_blank" rel="noopener noreferrer">
                    Open it in HubSpot ↗
                  </a>
                )}
              </div>
              <div className={styles.conflictNote}>Nothing was created or changed.</div>
            </div>
          )}

          {!done && (hero ? (
            <>
              <p className={styles.lead}>
                One hero per precinct or department, recognised for their years of service.
                {' '}<strong>{NO_COST_NOTE}</strong>
              </p>
              <Field label="Honoree name" required>
                <input className="input" value={heroName} onChange={(e) => setHeroName(e.target.value)} placeholder="e.g. Sgt. Ramona Vale" />
              </Field>
              <div className={styles.pair}>
                <Field label="Rank / title">
                  <input className="input" value={rank} onChange={(e) => setRank(e.target.value)} placeholder="Sergeant" />
                </Field>
                <Field label="Years of service">
                  <input className="input" type="number" min="0" max="70" value={years} onChange={(e) => setYears(e.target.value)} placeholder="27" />
                </Field>
              </div>
              <Field label="Citation — why this hero">
                <textarea className="input" rows={3} value={citation} onChange={(e) => setCitation(e.target.value)} placeholder="What the department is recognising." />
              </Field>
            </>
          ) : cheer ? (
            <>
              <p className={styles.lead}>
                Opens a partnership deal against this gym and puts it on the pipeline.
                {' '}<strong>{PARTNERSHIP_DEFINITION}</strong> This create does neither of those —
                it opens the pursuit; the signature and the program launch are human actions on the deal.
              </p>
              <Field label="What the partnership covers" required>
                <select className="input" value={product} onChange={(e) => setProduct(e.target.value)}>
                  {CHEER_PROGRAMS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Pursuit date">
                <input className="input" type="date" value={pursuitDate} onChange={(e) => setPursuitDate(e.target.value)} />
              </Field>
            </>
          ) : (
            <>
              <p className={styles.lead}>
                Opens a pursuit deal against this organization. The deal name IS the product
                pursued, so the board reads as the list of things we are actually going after here.
              </p>
              <Field label="Product pursued" required>
                <select className="input" value={product} onChange={(e) => setProduct(e.target.value)}>
                  {GREEK_PRODUCTS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Pursuit date">
                <input className="input" type="date" value={pursuitDate} onChange={(e) => setPursuitDate(e.target.value)} />
              </Field>
            </>
          ))}

          {!done && (
            <Field label="Assigned rep">
              <input className="input" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Unassigned" />
            </Field>
          )}

          {/* A DEMO RECORD CANNOT BE WRITTEN. Said plainly, with the reason. */}
          {demo && !done && (
            <div className={styles.warn}>
              <strong>Demo record — the write is off.</strong> This organization isn’t a HubSpot company
              (id <code>{record?.sourceId || 'none'}</code>), so no deal can be created against it. Load the
              Command Center without <code>?demo=1</code> to make a real nomination.
            </div>
          )}

          {/* WHAT HAPPENS WHEN YOU CLICK — said before the click, not after. */}
          {!done && !conflict && !demo && (
            <div className={styles.whatNext}>
              <strong>What happens next:</strong>{' '}
              {hero
                ? 'a Community Hero deal is created in HubSpot and associated to this department, owned by you, at no cost to the department. It then shows on this board as the department’s hero and the next step becomes producing the piece.'
                : cheer
                  ? 'a partnership deal is created in HubSpot and associated to this gym. The gym moves from TARGET to IN PURSUIT on the pipeline, and its next step becomes getting the contract signed. Nothing is marked signed and no jewelry program is launched.'
                  : 'a pursuit deal named for the product is created in HubSpot and associated to this organization. It then shows on this board with its owner and open date, and the next step becomes working it to won.'}
            </div>
          )}

          {/* The composed payload stays available — it is what the route
              receives, and it is worth being able to read before submitting. */}
          {!done && (
            <details className={styles.payload}>
              <summary>The deal this will create</summary>
              <pre className={styles.pre}>{JSON.stringify(payload, null, 2)}</pre>
            </details>
          )}

          {problems.length > 0 && !done && (
            <ul className={styles.problems}>
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}

          {error && <div className={styles.error}>{error}</div>}

          {/* THE REAL RESULT. `created` and `duplicate` are the backend's own
              verdict — the modal never claims a create that didn't happen. */}
          {done && (
            <div className={styles.ok}>
              <strong>
                {result.created
                  ? (hero ? 'Nomination created.' : cheer ? 'Partnership pursuit opened.' : 'Pursuit opened.')
                  : 'Already recorded — nothing was duplicated.'}
              </strong>
              <div>
                {result.created
                  ? <>{result.dealname} is now associated to {record?.name}.</>
                  : result.message}
              </div>
              {result.created && result.associated === false && (
                <div className={styles.warn}>
                  The deal was created but could NOT be associated to {record?.name} — associate it in HubSpot.
                </div>
              )}
              {result.created && result.droppedProperties?.length > 0 && (
                <div className={styles.warn}>
                  These properties don’t exist on the portal yet and were left off:{' '}
                  {result.droppedProperties.join(', ')}. The deal was created without them.
                </div>
              )}
              {createdUrl && (
                <a className={styles.okLink} href={createdUrl} target="_blank" rel="noopener noreferrer">
                  Open the deal in HubSpot ↗
                </a>
              )}
            </div>
          )}
        </div>

        <div className={styles.foot}>
          <button className="btn" onClick={onClose}>{done ? 'Done' : 'Cancel'}</button>
          {!done && (
            <button
              className="btn btn-primary"
              disabled={!ready || busy}
              title={demo ? 'Demo record — no HubSpot company to write against' : (!gate.allowed ? gate.reason : undefined)}
              onClick={submit}
            >
              {busy ? 'Creating…' : hero ? 'Nominate' : cheer ? 'Start partnership pursuit' : 'Start pursuit'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

function Field({ label, required, children }) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>
        {label}{required && <span className={styles.req}>required</span>}
      </span>
      {children}
    </label>
  )
}

export { COMMUNITY_HERO, GREEK_PURSUIT }
